import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import {
  ArrowLeft, Moon, Sun, Smile, Zap,
  Plus, Dumbbell, BarChart3, X, Check, AlertTriangle, TrendingUp,
} from "lucide-react"
import { useMonitoring } from "@/hooks/useMonitoring"
import { computeCurrentStatus, computeACWRSeries, computeStrainByCalendarWeek } from "@/lib/monitoring/calculations"
import { toLocalISODate, parseLocalDate, formatFr, startOfWeek } from "@/lib/utils"
import { BottomNav } from "@/components/BottomNav"
import { usePostSessionPending } from "@/hooks/usePostSessionPending"
import { formatUnlockTime } from "@/lib/supabase"
import { ClockGauge } from "@/components/monitoring/ClockGauge"

interface ZoneDef {
  from: number
  to: number
  color: string
  label: string
  opacity?: number
}

interface ChartDatum {
  key: string
  label: string
  fullLabel: string
  value: number | null
}

interface MetricConfig {
  title: string
  subtitle: string
  yMin: number
  yMax: number
  zones: ZoneDef[]
  decimals: number
  toggle: boolean
  emptyMessage: string
}

// Les zones "orange" adjacentes à une zone rouge reçoivent une opacité plus élevée (0,22
// au lieu de 0,15) — sinon orange et rouge sont visuellement indiscernables à faible opacité.
const ACWR_ZONES: ZoneDef[] = [
  { from: 0, to: 0.8, color: "#eab308", label: "Sous-entraînement" },
  { from: 0.8, to: 1.3, color: "#22c55e", label: "Optimal" },
  { from: 1.3, to: 1.5, color: "#f97316", label: "Vigilance", opacity: 0.22 },
  { from: 1.5, to: 2, color: "#ef4444", label: "Surcharge" },
]

const STRAIN_ZONES: ZoneDef[] = [
  { from: 0, to: 1500, color: "#eab308", label: "Charge faible" },
  { from: 1500, to: 3000, color: "#22c55e", label: "Optimal" },
  { from: 3000, to: 4500, color: "#f97316", label: "Vigilance", opacity: 0.22 },
  { from: 4500, to: 6000, color: "#ef4444", label: "Surcharge" },
]

const HOOPER_ZONES: ZoneDef[] = [
  { from: 4, to: 7, color: "#22c55e", label: "Excellent" },
  { from: 7, to: 15, color: "#06b6d4", label: "Bon" },
  { from: 15, to: 19, color: "#eab308", label: "Moyen" },
  { from: 19, to: 23, color: "#f97316", label: "Mauvais", opacity: 0.22 },
  { from: 23, to: 28, color: "#ef4444", label: "Très mauvais" },
]

const METRIC_CONFIG: Record<"acwr" | "strain" | "hooper", MetricConfig> = {
  acwr: {
    title: "Évolution Charge (ACWR)",
    subtitle: "Compare ta charge des 7 derniers jours à ta charge habituelle du dernier mois",
    yMin: 0,
    yMax: 2,
    zones: ACWR_ZONES,
    decimals: 2,
    toggle: true,
    emptyMessage: "Aucune séance enregistrée.",
  },
  strain: {
    title: "Évolution Strain",
    subtitle: "Indique à quel point ta charge d'entraînement de la semaine sollicite ton corps",
    yMin: 0,
    yMax: 6000,
    zones: STRAIN_ZONES,
    decimals: 0,
    toggle: false,
    emptyMessage: "Aucune séance enregistrée.",
  },
  hooper: {
    title: "Évolution Hooper",
    subtitle: "Ton état de forme au réveil : sommeil, fraîcheur, humeur et courbatures",
    yMin: 4,
    yMax: 28,
    zones: HOOPER_ZONES,
    decimals: 0,
    toggle: true,
    emptyMessage: "Aucune entrée Hooper encore.",
  },
}

// Axe Y : décimales significatives uniquement (0,8 · 1,3 · 1,5 — jamais 0,80)
function formatTickFr(n: number): string {
  return n.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2, useGrouping: false })
}

function zoneForValue(zones: ZoneDef[], value: number): ZoneDef {
  return zones.find((z) => value >= z.from && value <= z.to) ?? zones[zones.length - 1]
}

const DAY_ABBR = ["lun", "mar", "mer", "jeu", "ven", "sam", "dim"]
const DAY_FULL = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"]

// Vue Jour : les 7 jours de la semaine calendaire en cours (lundi→dimanche), pas une fenêtre glissante.
// Un jour sans donnée (pas encore arrivé, ou Hooper non rempli) reste à value=null — jamais interpolé.
function toDayView(raw: { date: string; value: number }[]): ChartDatum[] {
  const monday = startOfWeek()
  const map = new Map(raw.map((d) => [d.date, d.value]))
  return DAY_ABBR.map((label, i) => {
    const d = new Date(monday)
    d.setDate(monday.getDate() + i)
    const key = toLocalISODate(d)
    return { key, label, fullLabel: DAY_FULL[i], value: map.has(key) ? map.get(key)! : null }
  })
}

// 6 semaines calendaires fixes, quel que soit le jour de la semaine où on regarde : le nombre
// de buckets ne dépend jamais d'un nombre de jours de données disponibles.
const WEEKS_BACK = 5

// Lundi de la semaine en cours moins WEEKS_BACK semaines — point d'ancrage de la vue Semaine.
function weekViewStart(): Date {
  const monday = startOfWeek()
  const start = new Date(monday)
  start.setDate(monday.getDate() - WEEKS_BACK * 7)
  return start
}

// Différence en jours calendaires entre deux dates, insensible aux changements d'heure DST
// (ancrée sur des pseudo-dates UTC construites à partir des composantes locales, jamais sur
// une simple division de millisecondes qui casserait sur un jour de 23h/25h).
function daysBetween(a: Date, b: Date): number {
  const utcA = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate())
  const utcB = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate())
  return Math.round((utcB - utcA) / 86400000)
}

// Vue Semaine : exactement WEEKS_BACK + 1 buckets (S1 = la plus ancienne, dernier = semaine en
// cours), ancrés sur weekViewStart() — jamais déduits du nombre de semaines distinctes présentes
// dans les données. Une semaine sans donnée reste à value=null (jamais interpolée).
function toWeekView(raw: { date: string; value: number }[]): ChartDatum[] {
  const start = weekViewStart()
  const bucketCount = WEEKS_BACK + 1
  const buckets: number[][] = Array.from({ length: bucketCount }, () => [])
  for (const d of raw) {
    const dt = parseLocalDate(d.date)
    const weekIdx = Math.floor(daysBetween(start, dt) / 7)
    if (weekIdx >= 0 && weekIdx < bucketCount) buckets[weekIdx].push(d.value)
  }
  return buckets.map((values, i) => {
    const weekStart = new Date(start)
    weekStart.setDate(start.getDate() + i * 7)
    return {
      key: toLocalISODate(weekStart),
      label: `S${i + 1}`,
      fullLabel: `Semaine ${i + 1}`,
      value: values.length ? Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 100) / 100 : null,
    }
  })
}

interface DashboardPageProps {
  email: string
  firstName?: string
  offerId?: string
}

// WEEKS_BACK + 1 = 6 semaines calendaires, au plus 41 jours avant aujourd'hui (5 semaines pleines
// + jusqu'à 6 jours dans la semaine en cours si on regarde un dimanche) → fenêtre fixe de 42 jours,
// suffisante dans tous les cas quel que soit le jour de la semaine où on regarde.
const MONITORING_WINDOW_DAYS = 42

export const DashboardPage = ({}: DashboardPageProps) => {
  const navigate = useNavigate()
  const postSessionPending = usePostSessionPending()
  const postSessionAwaiting =
    postSessionPending?.kind === "program" ||
    (postSessionPending?.kind === "activity" && !postSessionPending.locked)
  const postSessionUnlockAt =
    postSessionPending?.kind === "activity" && postSessionPending.locked ? postSessionPending.endAt : null
  const { todayHooper, avg7Hooper, todayWellness, acwr, strain, wellness, loads, loading, error } =
    useMonitoring(MONITORING_WINDOW_DAYS)
  const [chartModal, setChartModal] = useState<"hooper" | "acwr" | "strain" | null>(null)
  const [chartMode, setChartMode] = useState<Record<"acwr" | "hooper", "jour" | "semaine">>({
    acwr: "jour",
    hooper: "jour",
  })

  const acwrSeries = computeACWRSeries(loads, MONITORING_WINDOW_DAYS)

  const hooperRaw = wellness.map((w) => ({ date: w.date, value: w.hooper_score }))
  const acwrRaw = acwrSeries.map((p) => ({ date: p.date, value: p.acwr }))

  // Même découpage calendaire et même seuil que la tuile Strain (computeStrain) : la
  // dernière barre de ce graphe EST la valeur de la tuile. Une semaine sans verdict
  // (moins de 2 jours chargés) vaut null et se rend comme un trou, jamais comme un 0
  // qui tomberait en zone jaune « Charge faible ».
  const strainWeekly = computeStrainByCalendarWeek(loads, weekViewStart(), WEEKS_BACK)
  const strainChartData: ChartDatum[] = strainWeekly.map((wk, i) => ({
    key: wk.weekStart,
    label: `S${i + 1}`,
    fullLabel: `Semaine ${i + 1}`,
    value: wk.strain,
  }))

  // Hooper sub-metrics (depuis l'entrée du jour ou la moyenne 7j)
  const hooperBreakdown = todayWellness ?? null
  const hooperScore = todayWellness?.hooper_score ?? avg7Hooper ?? 0
  const hooperZone = todayHooper ?? (avg7Hooper ? { label: "—", color: "#94a3b8", description: "" } : { label: "—", color: "#94a3b8", description: "" })

  const currentStatus = computeCurrentStatus(acwr, strain, todayHooper ?? null)

  return (
    <div className="min-h-screen bg-[#060a13] cursor-default pb-24 relative">
      {/* HEADER */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="px-5 pt-5 pb-3 relative z-10"
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/dashboard")}
            className="w-9 h-9 rounded-lg bg-gray-800/50 flex items-center justify-center hover:bg-gray-700/50 cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4 text-gray-400" />
          </button>
          <h1 className="text-[20px] font-bold text-white">Monitoring</h1>
        </div>
      </motion.div>

      {/* QUICK ACTIONS */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="px-4 mt-2 relative z-10 grid grid-cols-2 gap-3"
      >
        <button
          onClick={() => navigate("/hooper")}
          className="rounded-2xl py-3.5 flex items-center justify-center gap-2 cursor-pointer transition-all active:scale-[0.97]"
          style={{
            background: "rgba(17,24,39,0.7)",
            border: "1px solid rgba(31,41,55,0.6)",
            backdropFilter: "blur(12px)",
          }}
        >
          <Moon className="w-4 h-4 text-violet-400" />
          <span className="text-[13px] font-bold text-white">Hooper du matin</span>
        </button>
        {/* Indicateur : pastille = un questionnaire attend (séance ou activité terminée) ;
            « disponible à HHhMM » = seule une activité pas encore terminée attend ;
            rien sinon. Le bouton reste toujours là : c'est l'accès fixe au questionnaire. */}
        <button
          onClick={() => navigate("/post-session")}
          aria-label={postSessionAwaiting ? "Post-séance — questionnaire à remplir" : undefined}
          className="relative rounded-2xl py-3.5 flex flex-col items-center justify-center cursor-pointer transition-all active:scale-[0.97]"
          style={{
            background: "rgba(17,24,39,0.7)",
            border: postSessionAwaiting ? "1px solid rgba(6,182,212,0.35)" : "1px solid rgba(31,41,55,0.6)",
            backdropFilter: "blur(12px)",
          }}
        >
          <span className="flex items-center gap-2">
            <Dumbbell className="w-4 h-4 text-cyan-400" />
            <span className="text-[13px] font-bold text-white">Post-séance</span>
          </span>
          {postSessionUnlockAt && (
            <span className="text-[10.5px] text-gray-500 mt-0.5 tabular-nums">
              disponible à {formatUnlockTime(postSessionUnlockAt)}
            </span>
          )}
          {postSessionAwaiting && (
            <span aria-hidden className="absolute top-2 right-2.5 w-2 h-2 rounded-full"
              style={{ background: "#06b6d4", boxShadow: "0 0 8px rgba(6,182,212,0.7)" }} />
          )}
        </button>
      </motion.div>

      {error && (
        <div
          className="mx-4 mt-3 p-3 rounded-xl text-[13px]"
          style={{
            background: "rgba(239,68,68,0.10)",
            border: "1px solid rgba(239,68,68,0.25)",
            color: "#f87171",
          }}
        >
          {error}
        </div>
      )}

      {loading && (
        <p className="text-center text-[12px] text-gray-500 mt-6">Chargement…</p>
      )}

      {/* HOOPER KPI */}
      {!loading && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="px-4 mt-3 relative z-10"
        >
          <div
            className="rounded-2xl p-4 relative overflow-hidden"
            style={{
              background: "rgba(17,24,39,0.7)",
              border: "1px solid rgba(31,41,55,0.6)",
              backdropFilter: "blur(12px)",
            }}
          >
            <div
              className="absolute -top-10 -right-10 w-[140px] h-[140px] rounded-full pointer-events-none"
              style={{ background: `radial-gradient(circle, ${hooperZone.color}1c, transparent 70%)` }}
            />

            <div className="flex items-center justify-between mb-3.5">
              <p className="text-[10px] uppercase tracking-wider text-gray-500">
                INDICE DE RÉCUPÉRATION <span className="text-gray-600 normal-case">— Hooper</span>
              </p>
              <button
                onClick={() => setChartModal("hooper")}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] cursor-pointer hover:bg-white/[0.03]"
                style={{ border: "1px solid rgba(31,41,55,0.6)", color: "#94a3b8" }}
              >
                <BarChart3 className="w-3 h-3" />
                Détails
              </button>
            </div>

            <div className="flex items-center gap-3.5">
              <ClockGauge value={hooperScore} max={METRIC_CONFIG.hooper.yMax} color={hooperZone.color} size={88} />
              <div className="flex-1 min-w-0">
                <p className="text-[22px] font-bold leading-tight" style={{ color: hooperZone.color }}>
                  {hooperZone.label}
                </p>
                <p className="text-[11px] text-gray-500 mt-1 tabular-nums">Score {hooperScore} / 28</p>
              </div>
            </div>

            {/* Sub-metrics */}
            <div className="grid grid-cols-2 gap-2 mt-3.5">
              <SubMetric label="Sommeil" value={hooperBreakdown?.sleep_quality ?? "—"} icon={Moon} />
              <SubMetric label="Fraîcheur" value={hooperBreakdown?.muscle_freshness ?? "—"} icon={Sun} />
              <SubMetric label="Humeur" value={hooperBreakdown?.mood ?? "—"} icon={Smile} />
              <SubMetric label="Courbatures" value={hooperBreakdown?.soreness ?? "—"} icon={Zap} />
            </div>
          </div>
        </motion.div>
      )}

      {/* ACWR KPI */}
      {!loading && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="px-4 mt-3 relative z-10"
        >
          <div
            className="rounded-2xl p-4 relative overflow-hidden"
            style={{
              background: "rgba(17,24,39,0.7)",
              border: "1px solid rgba(31,41,55,0.6)",
              backdropFilter: "blur(12px)",
            }}
          >
            <div
              className="absolute -top-10 -right-10 w-[140px] h-[140px] rounded-full pointer-events-none"
              style={{ background: `radial-gradient(circle, ${acwr.color}1c, transparent 70%)` }}
            />

            <div className="flex items-center justify-between mb-3.5">
              <p className="text-[10px] uppercase tracking-wider text-gray-500">
                CHARGE D'ENTRAÎNEMENT <span className="text-gray-600 normal-case">— ACWR</span>
              </p>
              <button
                onClick={() => setChartModal("acwr")}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] cursor-pointer hover:bg-white/[0.03]"
                style={{ border: "1px solid rgba(31,41,55,0.6)", color: "#94a3b8" }}
              >
                <BarChart3 className="w-3 h-3" />
                Détails
              </button>
            </div>

            <div className="flex items-center gap-3.5">
              <ClockGauge
                value={acwr.acwr}
                max={METRIC_CONFIG.acwr.yMax}
                color={acwr.color}
                size={88}
                valueSize={20}
                format={(n) => n.toFixed(2)}
              />
              <div className="flex-1 min-w-0">
                <p className="text-[20px] font-bold leading-tight" style={{ color: acwr.color }}>
                  {acwr.label === "Optimal" ? "Zone optimale" : acwr.label}
                </p>
                <p className="text-[11px] text-gray-500 mt-1">Ratio charge aiguë/chronique</p>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* STRAIN KPI */}
      {!loading && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="px-4 mt-3 relative z-10"
        >
          <div
            className="rounded-2xl p-4 relative overflow-hidden"
            style={{
              background: "rgba(17,24,39,0.7)",
              border: "1px solid rgba(31,41,55,0.6)",
              backdropFilter: "blur(12px)",
            }}
          >
            <div
              className="absolute -top-10 -right-10 w-[140px] h-[140px] rounded-full pointer-events-none"
              style={{ background: `radial-gradient(circle, ${strain.color}1c, transparent 70%)` }}
            />

            <div className="flex items-center justify-between mb-3.5">
              <p className="text-[10px] uppercase tracking-wider text-gray-500">
                FATIGUE CUMULÉE <span className="text-gray-600 normal-case">— Strain</span>
              </p>
              <button
                onClick={() => setChartModal("strain")}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] cursor-pointer hover:bg-white/[0.03]"
                style={{ border: "1px solid rgba(31,41,55,0.6)", color: "#94a3b8" }}
              >
                <BarChart3 className="w-3 h-3" />
                Détails
              </button>
            </div>

            <div className="flex items-center gap-3.5">
              <ClockGauge
                value={strain.strain}
                max={METRIC_CONFIG.strain.yMax}
                color={strain.color}
                size={88}
                valueSize={18}
                format={(n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${Math.round(n)}`)}
              />
              <div className="flex-1 min-w-0">
                <p className="text-[20px] font-bold leading-tight" style={{ color: strain.color }}>
                  {strain.label}
                </p>
                <p className="text-[11px] text-gray-500 mt-1">Charge × monotonie</p>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* ÉTAT DE FORME */}
      {!loading && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="px-4 mt-3 relative z-10"
        >
          <div
            className="rounded-2xl p-4"
            style={{
              background: "rgba(12,20,35,0.70)",
              border: `1px solid ${currentStatus.color}40`,
            }}
          >
            <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-3">
              ÉTAT DE FORME
            </p>

            <div className="flex items-center gap-3 mb-4">
              <div
                className={`w-9 h-9 rounded-lg flex items-center justify-center text-[14px] font-extrabold${currentStatus.level === 5 ? " status-alert-5" : ""}`}
                style={{ background: `${currentStatus.color}15`, color: currentStatus.color, border: `1px solid ${currentStatus.color}30` }}
              >
                {currentStatus.level}
              </div>
              <p className="text-[15px] font-bold" style={{ color: currentStatus.color }}>
                {currentStatus.label}
              </p>
            </div>

            <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">ANALYSE</p>
            <ul className="space-y-1.5 mb-4">
              {currentStatus.analysis.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-[12px] text-gray-300">
                  <span className="text-gray-600 mt-0.5">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">
              RECOMMANDATION
            </p>
            <div
              className="rounded-lg p-3 text-[12px] text-gray-300 leading-relaxed"
              style={{
                background: "rgba(0,0,0,0.25)",
                border: "1px solid rgba(30,41,59,0.55)",
              }}
            >
              {currentStatus.recommendation}
            </div>
          </div>
        </motion.div>
      )}

      {/* CHART MODAL */}
      <AnimatePresence>
        {chartModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-[4px]"
              onClick={() => setChartModal(null)}
            />
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 350, damping: 30 }}
              className="fixed z-[100] bottom-0 left-0 right-0 p-5 pb-8 rounded-t-3xl max-h-[80vh] overflow-y-auto"
              style={{
                background: "linear-gradient(180deg,#0f1829,#0a1020)",
                border: "1px solid rgba(30,41,59,0.55)",
              }}
            >
              <div className="flex justify-center mb-3">
                <div className="w-10 h-1 rounded-full bg-white/10" />
              </div>

              {(() => {
                const key = chartModal
                const config = METRIC_CONFIG[key]
                const mode = config.toggle ? chartMode[key as "acwr" | "hooper"] : "semaine"
                const data =
                  key === "strain"
                    ? strainChartData
                    : mode === "jour"
                      ? toDayView(key === "hooper" ? hooperRaw : acwrRaw)
                      : toWeekView(key === "hooper" ? hooperRaw : acwrRaw)
                const hasData = data.some((d) => d.value !== null)
                const todayIndex = mode === "jour" ? data.findIndex((d) => d.key === toLocalISODate(new Date())) : -1

                return (
                  <>
                    <div className="flex items-start justify-between mb-1">
                      <h3 style={{ fontSize: 17, fontWeight: 500, color: "#e5e7eb" }}>{config.title}</h3>
                      <button
                        onClick={() => setChartModal(null)}
                        className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer hover:bg-white/[0.04] shrink-0"
                      >
                        <X className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>
                    <p style={{ fontSize: 12, color: "#9ca3af", lineHeight: 1.5 }} className="mb-3">
                      {config.subtitle}
                    </p>

                    {/* Décomposition de la semaine EN COURS — masquée tant qu'elle n'a pas
                        de verdict, sinon elle afficherait « 0 UA × 0 », un faux zéro. */}
                    {key === "strain" && strain.hasData && (
                      <p
                        className="mx-auto mb-4 w-fit text-center"
                        style={{
                          background: "rgba(31,41,55,0.5)",
                          borderRadius: 6,
                          padding: "5px 12px",
                          fontFamily: "monospace",
                          fontSize: 11,
                          color: "#9ca3af",
                        }}
                      >
                        Charge sem. <span style={{ color: "#e5e7eb" }}>{strain.weeklyLoad} UA</span>
                        {"  ×  "}
                        Monotonie <span style={{ color: "#e5e7eb" }}>{formatFr(strain.monotony, 2)}</span>
                      </p>
                    )}

                    {config.toggle && (
                      <div className="flex justify-center gap-1.5 mb-4 p-1 rounded-lg w-fit mx-auto" style={{ background: "rgba(0,0,0,0.25)" }}>
                        {(["jour", "semaine"] as const).map((m) => (
                          <button
                            key={m}
                            onClick={() => setChartMode((prev) => ({ ...prev, [key as "acwr" | "hooper"]: m }))}
                            className="px-3 py-1 rounded-md text-[12px] font-semibold cursor-pointer capitalize transition-colors"
                            style={
                              mode === m
                                ? { background: "rgba(6,182,212,0.20)", color: "#22d3ee" }
                                : { color: "#64748b" }
                            }
                          >
                            {m}
                          </button>
                        ))}
                      </div>
                    )}

                    {!hasData ? (
                      <p className="text-[13px] text-gray-500 text-center py-8">{config.emptyMessage}</p>
                    ) : (
                      <MonitoringChart data={data} config={config} todayIndex={todayIndex} />
                    )}

                    <ChartLegend zones={config.zones} />
                  </>
                )
              })()}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <style>{`
        @keyframes alertPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(220,38,38,0.5); }
          50% { box-shadow: 0 0 0 6px rgba(220,38,38,0); }
        }
        .status-alert-5 { animation: alertPulse 1.4s ease-in-out infinite; }
      `}</style>

      <BottomNav />
    </div>
  )
}

// ============================================
// SUB METRIC (sommeil/fraîcheur/humeur/courbatures)
// ============================================

function SubMetric({
  label,
  value,
  icon: IconComp,
}: {
  label: string
  value: number | string
  icon?: React.ComponentType<{ className?: string; style?: React.CSSProperties }>
}) {
  const numValue = typeof value === "number" ? value : 0
  const color =
    typeof value !== "number"
      ? "#64748b"
      : numValue <= 2
      ? "#22c55e"
      : numValue <= 4
      ? "#eab308"
      : numValue <= 5
      ? "#f97316"
      : "#ef4444"

  return (
    <div
      className="rounded-xl px-3 py-2.5 flex items-center justify-between gap-2"
      style={{
        background: "rgba(255,255,255,0.025)",
        border: "1px solid rgba(31,41,55,0.6)",
      }}
    >
      <div className="flex items-center gap-2 min-w-0">
        {IconComp && <IconComp className="w-3.5 h-3.5 shrink-0" style={{ color }} />}
        <span className="text-[12px] text-gray-400 truncate">{label}</span>
      </div>
      <span className="text-[14px] font-bold shrink-0" style={{ color }}>
        {value}
      </span>
    </div>
  )
}

// ============================================
// MONITORING CHART — axe fixe, zones, tooltip, gaps non interpolés
// ============================================

function MonitoringChart({
  data,
  config,
  todayIndex = -1,
}: {
  data: ChartDatum[]
  config: MetricConfig
  todayIndex?: number
}) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)

  const width = 320
  const height = 170
  const leftPad = 34
  const rightPad = 20
  const topPad = 8
  const bottomPad = 22
  const plotWidth = width - leftPad - rightPad
  const plotHeight = height - topPad - bottomPad
  const range = config.yMax - config.yMin || 1

  // Une valeur hors des bornes de l'axe (ex. ACWR > 2,0) est clampée à l'extrémité du tracé
  // plutôt que positionnée hors du viewBox — sinon le point est coupé silencieusement par le SVG.
  const valueToY = (v: number) => {
    const clamped = Math.max(config.yMin, Math.min(config.yMax, v))
    return topPad + plotHeight - ((clamped - config.yMin) / range) * plotHeight
  }

  const n = data.length
  const points = data.map((d, i) => ({
    x: leftPad + (n > 1 ? (i / (n - 1)) * plotWidth : plotWidth / 2),
    y: d.value !== null ? valueToY(d.value) : null,
    value: d.value,
    label: d.label,
    fullLabel: d.fullLabel,
  }))

  const knownIdx = points.map((p, i) => (p.value !== null ? i : -1)).filter((i) => i >= 0)
  const lastKnownIdx = knownIdx.length > 0 ? knownIdx[knownIdx.length - 1] : -1

  const segments: { a: number; b: number; dashed: boolean }[] = []
  for (let k = 0; k < knownIdx.length - 1; k++) {
    const a = knownIdx[k]
    const b = knownIdx[k + 1]
    segments.push({ a, b, dashed: b - a > 1 })
  }

  const ticks = config.zones.slice(1).map((z) => z.from)

  const active = activeIndex !== null ? points[activeIndex] : null

  return (
    <div className="relative">
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        {config.zones.map((z, i) => {
          const yTop = valueToY(Math.min(config.yMax, z.to))
          const yBottom = valueToY(Math.max(config.yMin, z.from))
          return (
            <rect
              key={i}
              x={leftPad}
              y={yTop}
              width={plotWidth}
              height={Math.max(0, yBottom - yTop)}
              fill={z.color}
              opacity={z.opacity ?? 0.12}
            />
          )
        })}

        {ticks.map((t, i) => {
          const y = valueToY(t)
          return (
            <g key={i}>
              <line x1={leftPad} x2={width - rightPad} y1={y} y2={y} stroke="#ffffff" strokeOpacity={0.2} strokeWidth={1} />
              <text x={leftPad - 6} y={y + 3} textAnchor="end" fontSize={11} fill="#6b7280">
                {formatTickFr(t)}
              </text>
            </g>
          )
        })}

        {todayIndex >= 0 && (
          <line
            x1={leftPad + (n > 1 ? (todayIndex / (n - 1)) * plotWidth : plotWidth / 2)}
            x2={leftPad + (n > 1 ? (todayIndex / (n - 1)) * plotWidth : plotWidth / 2)}
            y1={topPad}
            y2={topPad + plotHeight}
            stroke="#ffffff"
            strokeOpacity={0.15}
            strokeWidth={1}
          />
        )}

        {segments.map((s, i) => {
          const p1 = points[s.a]
          const p2 = points[s.b]
          if (p1.y === null || p2.y === null) return null
          return (
            <line
              key={i}
              x1={p1.x}
              y1={p1.y}
              x2={p2.x}
              y2={p2.y}
              stroke="#ffffff"
              strokeWidth={2}
              strokeLinecap="round"
              strokeDasharray={s.dashed ? "3 5" : undefined}
              opacity={s.dashed ? 0.45 : 1}
            />
          )
        })}

        {knownIdx.map((i) => {
          const p = points[i]
          const isLast = i === lastKnownIdx
          return (
            <circle
              key={i}
              cx={p.x}
              cy={p.y as number}
              r={isLast ? 5 : 3.5}
              fill="#ffffff"
              stroke={isLast ? "#060a13" : "none"}
              strokeWidth={isLast ? 2 : 0}
              style={{ cursor: "pointer" }}
              onMouseEnter={() => setActiveIndex(i)}
              onMouseLeave={() => setActiveIndex(null)}
              onClick={() => setActiveIndex((prev) => (prev === i ? null : i))}
            />
          )
        })}

        {points.map((p, i) => (
          <text
            key={i}
            x={p.x}
            y={height - 4}
            textAnchor="middle"
            fontSize={11}
            fill={i === todayIndex ? "#e5e7eb" : p.value === null ? "#4b5563" : i === n - 1 ? "#e5e7eb" : "#6b7280"}
          >
            {p.label}
          </text>
        ))}
      </svg>

      {active && active.value !== null && (() => {
        const zone = zoneForValue(config.zones, active.value)
        const xPct = (active.x / width) * 100
        const yPct = ((active.y as number) / height) * 100
        const anchor = xPct < 18 ? "left" : xPct > 82 ? "right" : "center"
        const translateX = anchor === "left" ? "0%" : anchor === "right" ? "-100%" : "-50%"
        return (
          <div
            className="absolute pointer-events-none px-2.5 py-1.5 rounded-md text-center"
            style={{
              left: `${xPct}%`,
              top: `${yPct}%`,
              transform: `translate(${translateX}, -130%)`,
              background: "#1f2937",
              border: "1px solid #374151",
              whiteSpace: "nowrap",
            }}
          >
            <p style={{ fontSize: 10, color: "#9ca3af" }}>{active.fullLabel}</p>
            <p style={{ fontSize: 12, color: "#ffffff", fontWeight: 500 }}>
              {formatFr(active.value, config.decimals)} · {zone.label.toLowerCase()}
            </p>
          </div>
        )
      })()}
    </div>
  )
}

function ChartLegend({ zones }: { zones: ZoneDef[] }) {
  return (
    <div className="flex flex-wrap justify-center mt-3" style={{ gap: "10px 14px" }}>
      {zones.map((z, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <span
            style={{ width: 9, height: 9, borderRadius: 2, background: z.color, opacity: 0.7, display: "inline-block" }}
          />
          <span style={{ fontSize: 11, color: "#9ca3af" }}>{z.label}</span>
        </div>
      ))}
    </div>
  )
}

// utilisé par d'autres pages éventuellement
export { TrendingUp, AlertTriangle, Plus, Check }
